import { Table } from '@/components/ui/table';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { useMemo } from 'react';
import { DropEffect } from '@/components/drag-drop';

export function ObjectsTable({
  data,
  isLoading,
  onDelete,
  onEdit,
  setDragData,
  onAction,
  onActionStart,
  disableDrag,
  customColumns,
}: {
  data: any[];
  isLoading?: boolean;
  onDelete?: (uuid: string) => void;
  onEdit?: (uuid: string) => void;
  setDragData?: (uuid: string, label?: string) => void;
  onAction?: (row: any) => React.ReactNode;
  // rendered before the built-in actions, away from delete
  onActionStart?: (row: any) => React.ReactNode;
  disableDrag?: boolean;
  customColumns?: any[];
}) {
  function handleDragStart(
    event: React.DragEvent<HTMLButtonElement>,
    uuid: string,
    label: string
  ) {
    event.dataTransfer.setData('text/plain', setDragData?.(uuid, label) || '');
    event.dataTransfer.dropEffect = DropEffect.Copy;
  }

  const defaultColumns = useMemo(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        cell: ({ row }: { row: any }) => row.original.name,
      },
      {
        header: '',
        accessorKey: 'actions',
        cell: ({ row }: { row: any }) => {
          return (
            <div className="flex gap-x-2 justify-end">
              {onActionStart ? onActionStart(row.original) : null}
              {!disableDrag ? (
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 p-2 rounded-md bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700"
                  draggable
                  onDragStart={(event) =>
                    handleDragStart(event, row.original.uuid, row.original.name)
                  }
                >
                  <DocumentDuplicateIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                    focusable={false}
                  />
                </button>
              ) : null}
              {onEdit ? (
                <button
                  className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 p-2 rounded-md bg-blue-50 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800"
                  onClick={() => onEdit(row.original.uuid)}
                >
                  <PencilIcon
                    className="h-4 w-4 pointer-events-none"
                    aria-hidden="true"
                    focusable={false}
                  />
                </button>
              ) : null}
              {onDelete ? (
                <button
                  className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 p-2 rounded-md bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800"
                  onClick={() => onDelete(row.original.uuid)}
                >
                  <TrashIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                    focusable={false}
                  />
                </button>
              ) : null}
              {onAction ? onAction(row.original) : null}
            </div>
          );
        },
      },
    ],
    [onEdit, onDelete, onAction, onActionStart]
  );

  const columns = useMemo(() => {
    if (customColumns && customColumns.length > 0) {
      const nameColumn = defaultColumns[0];
      const actionsColumn = defaultColumns[defaultColumns.length - 1];
      return [nameColumn, ...customColumns, actionsColumn];
    }
    return defaultColumns;
  }, [defaultColumns, customColumns]);

  return <Table columns={columns} data={data} isLoading={isLoading} />;
}
